import { Link } from 'react-router-dom';
import moment from 'moment';
import HeaderWithLines from './HeaderWithLines';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import Spinner from './Spinner';
import User from './User';
import DateRangeBadge from './DateRangeBadge';
import LoadMoreSentinel from './LoadMoreSentinel';
import { useFirebase } from './FirebaseContextProvider';
import { useHomeTournaments } from '../utils/hooks';
import { getBaseRoute } from './LoginCard';
import { formatGenders, formatAges } from '../utils/misc';
import feat1 from '../assets/images/feat1.png';
import feat2 from '../assets/images/feat2.png';
import feat3 from '../assets/images/feat3.png';
import feat4 from '../assets/images/feat4.png';
import feat5 from '../assets/images/feat5.png';
import feat6 from '../assets/images/feat6.png';
import feat7 from '../assets/images/feat7.png';
import feat8 from '../assets/images/feat8.png';
import feat9 from '../assets/images/feat9.png';
import feat10 from '../assets/images/feat10.png';
import feat11 from '../assets/images/feat11.png';
import feat12 from '../assets/images/feat12.png';
import { UsersRoles } from '../utils/enums';
import placeholderTournament from '../assets/images/placeholder-tournament.png';
import './Home.scss';

const FEATURES = [
  {
    title: 'AI facial-recognition check-in',
    benefit:
      'Speeds up check-in dramatically and reduces errors from manual name matching.',
    img: feat2,
  },
  {
    title: 'Event planning',
    benefit:
      'Set up tournaments faster with clearer structure and fewer manual steps.',
    img: feat3,
  },
  {
    title: 'AI age verification',
    benefit:
      'Automates age checks so organizers and teams spend less time on verification and more time on the game.',
    img: feat9,
  },
  {
    title: 'Automated schedule generation',
    benefit:
      'Administrators can generate a complete schedule in minutes and feel confident there are no overlaps or conflicts.',
    img: feat4,
  },
  {
    title: 'AI referee game counting',
    benefit:
      'Automatically tracks referee appearances on the field using video detection to ensure all officiated games are counted correctly.',
    img: feat11,
  },
  {
    title: 'Fee collection',
    benefit:
      'Automates tournament fee collection so organizers spend less time chasing payments and can track who’s paid in one place.',
    img: feat6,
  },
  {
    title: 'Team rostering',
    benefit:
      'Keeps players and coaches organized so eligibility and participation are always up to date.',
    img: feat10,
  },
  {
    title: 'Digital waiver signing',
    benefit:
      'Collects waivers quickly and centrally, reducing paperwork and last-minute delays.',
    img: feat7,
  },
  {
    title: 'Fast automatic team seeding',
    benefit:
      'Creates brackets instantly based on rules, so playoffs can be generated in seconds.',
    img: feat8,
  },
  {
    title: 'Live team rankings',
    benefit:
      'Updates standings in real time so teams and spectators always see the latest picture.',
    img: feat5,
  },
  {
    title: "Referees' game counting & payout calculation",
    benefit:
      'Automatically tracks referee assignments and calculates totals for accurate payouts and reporting.',
    img: feat1,
  },
  {
    title: 'Staff payouts',
    benefit:
      'Automatically calculates staff payments based on assignments and rules, reducing manual accounting and payment mistakes.',
    img: feat12,
  },
];

export default function Home() {
  const { user } = useFirebase();
  const { tournaments, isLoading, isLoadingMore, hasMore, loadMore } =
    useHomeTournaments();

  return (
    <div className='home'>
      {/* HERO */}
      <section className='hero'>
        <div className='container'>
          <div className='hero-inner'>
            <Link to='#' className='logo-link'>
              <div className='logo bg-image' />
            </Link>

            <h2 className='tagline'>
              Manage sport events
              <br />
              faster, easier, smarter
            </h2>
          </div>
          <div className='top-inner'>
            {user && (
              <Link to={getBaseRoute(user)} className='link-to-login'>
                <p className='fa-enter' />
                <p className='enter-text'>
                  {user.role === UsersRoles.ADMIN ? 'Admin panel' : 'Profile'}
                </p>
              </Link>
            )}
            <User />
          </div>
          <a
            className='nvidia-logo'
            href='https://www.nvidia.com/en-us/startups/'
            target='_blank'
            rel='noopener noreferrer'
          >
            <div className='nvidia-logo-img bg-image' />
          </a>
        </div>
      </section>

      {/* TOURNAMENTS */}
      <section className='section tournaments-section'>
        <div className='container'>
          <HeaderWithLines>Tournaments</HeaderWithLines>
          <div className='tournaments-grid'>
            {tournaments.map((t) => {
              const isFinished = moment().isAfter(t.endDate);

              return (
                <Link
                  key={t.id}
                  to={'/tournaments/' + t.id}
                  className={
                    'tournament block left-accent' +
                    (isFinished ? ' is-finished' : '')
                  }
                >
                  <div className='tournament-media'>
                    <div
                      className='bg-image'
                      style={{
                        backgroundImage: `url(${t.imgThumbUrl || placeholderTournament})`,
                      }}
                    />
                    {isFinished && (
                      <div className='tournament-badge badge'>Finished</div>
                    )}
                  </div>

                  <div className='tournament-body'>
                    <div className='img-title-date'>
                      <div
                        className='bg-image wh-96'
                        style={{
                          backgroundImage: `url(${t.imgThumbUrl || placeholderTournament})`,
                        }}
                      >
                        {isFinished && (
                          <div className='tournament-badge badge'>Finished</div>
                        )}
                      </div>
                      <div className='title-date'>
                        <h4 className='tournament-title'>{t.name}</h4>
                        <DateRangeBadge start={t.startDate} end={t.endDate} />
                      </div>
                    </div>
                    <PropItemsBlock>
                      <PropItem prop='Location' value={t.location} />
                      <PropItem prop='Sports' value={t.sports.join(', ')} />
                      <PropItem
                        prop='Division'
                        value={`${formatGenders(t.genders)}, ${formatAges(
                          t.ages,
                        )}`}
                      />
                    </PropItemsBlock>
                  </div>
                </Link>
              );
            })}
            <Spinner show={isLoading} />
          </div>
          {/* Paging spinner sits below the grid, where the next rows appear;
              the overlay above only covers the first page, when there is no
              grid to cover yet */}
          <LoadMoreSentinel
            hasMore={hasMore}
            isLoading={isLoadingMore}
            onLoadMore={loadMore}
          />
        </div>
      </section>

      {/* FEATURES */}
      <section className='section'>
        <div className='container features'>
          <HeaderWithLines>Features</HeaderWithLines>
          <ul className='features-grid'>
            {FEATURES.map((f) => (
              <li className='feature' key={f.title}>
                <div className='img-title'>
                  <div
                    className='bg-image wh-64'
                    style={{ backgroundImage: `url(${f.img})` }}
                  />
                  <p className='feature-title upper font-medium'>{f.title}</p>
                </div>
                <p className='feature-benefit'>{f.benefit}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ABOUT */}
      <section className='section'>
        <div className='container about'>
          <HeaderWithLines>About</HeaderWithLines>
          <div className='block about'>
            <div className='about-body'>
              <h6>Intelligent sports managment from End to End</h6>
              <p className='about-p'>
                Muva Sport is an intelligent platform for managing sports events
                end to end. Our goal is to make tournament organization smooth
                and predictable for everyone involved — administrators, coaches,
                players, parents, referees, and fans.
              </p>
              <p className='about-p'>
                From planning to the final results, Muva Sport supports the full
                event workflow: event planning, automated schedule generation,
                team rostering, fee collection, referee assignments, player age
                verification, fast check-in, scoring, automatic seeding, live
                rankings, and staff payouts.
              </p>
              <p className='about-p'>
                Coaches can register teams quickly and keep rosters organized.
                Organizers get a clear view of teams, athletes, referees, and
                schedules in one place. Referees can check assignments and daily
                field or court locations without confusion. Muva Sport also
                streamlines payouts through integrated payment partners, helping
                officials and staff get paid accurately and on time.
              </p>
              <p className='about-p highlight'>
                Muva Sport uses AI to deliver more accurate verification and a
                faster check-in experience for athletes and coaches. Muva Sport
                is <span className='font-medium'>faster</span>.
              </p>
              <p className='about-p highlight'>
                Muva Sport uses an intelligent generative scheduling algorithm
                to plan the time and location of every match. Administrators can
                generate a complete schedule in minutes and feel confident there
                are no overlaps or conflicts. Muva Sport is{' '}
                <span className='font-medium'>easier</span>.
              </p>
              <p className='about-p highlight'>
                Muva Sport uses AI to review uploaded videos and make it easier
                to find key moments and build a highlight reel for each event.
                Muva Sport is <span className='font-medium'>smarter</span>.
              </p>
              <p className='about-p'>
                The result is simple: events run{' '}
                <span className='font-medium'>faster, easier, and smarter</span>{' '}
                — for staff and participants alike.
              </p>
            </div>
            <div className='bg-image' />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className='footer'>
        <div className='container'>
          <p className='footer-text'>© Muva</p>
        </div>
      </footer>
    </div>
  );
}
